import { rect, Rect } from '@geom';
import { canvasKit } from '@resources';
import { type Canvas, type Path as CKPath, type Surface } from 'canvaskit';
import { copyPath, deletePath } from './ckAllocation';

export function userSpaceBounds(
  canvas: Canvas,
  surface: Surface
): { bounds: Rect; scale: number } {
  const width = surface.width() + 1; // Add 1 to get the inclusive right bound
  const height = surface.height() + 1; // Add 1 to get the inclusive bottom bound

  // Get the current transformation matrix
  const ctm = canvas.getTotalMatrix();

  // Invert the CTM to go from device space to user space
  const invertedCTM = canvasKit.Matrix.invert(ctm);
  if (!invertedCTM) {
    throw new Error('Failed to invert the current transformation matrix');
  }

  // Now apply the inverted CTM to the device bounds to get the bounds in user space (points)
  const topLeft = canvasKit.Matrix.mapPoints(invertedCTM, [0, 0]); // Transform (0, 0)
  const bottomRight = canvasKit.Matrix.mapPoints(invertedCTM, [width, height]); // Transform (width + 1, height + 1)

  const scale = ctm[0];

  return {
    bounds: rect(topLeft[0], topLeft[1], bottomRight[0], bottomRight[1]),
    scale
  };
}

export function growPath(path: CKPath, width: number): CKPath {
  const grow = copyPath(path);
  grow.stroke({ width: width * 2 })?.op(path, canvasKit.PathOp.Union);
  return grow;
}

export function shrinkPath(path: CKPath, width: number): CKPath {
  const shrink = copyPath(path);
  shrink.stroke({ width })?.op(path, canvasKit.PathOp.ReverseDifference);
  return shrink;
}

export function inlinePath(path: CKPath, width: number): CKPath {
  const shrink = shrinkPath(path, width);
  const inlined = copyPath(shrink).stroke({ width });
  if (!inlined) {
    throw new Error('Failed to inline path');
  }
  deletePath(shrink);
  return inlined;
}
